// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Components
import { Header } from '@/components/layout/Header';
import { NotFound } from '@/pages/NotFound';
import { PermissionWrapper } from '@/components/PermissionWrapper';
import SomeComponent from '@/pages/SomeComponent';
import { PageListMain, PageFormMain } from '@/components/page-content-v4';

// Contexts and Providers
import { PageProvider } from '@/contexts/PageContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import { ProjectProvider } from '@/components/create-project';

// Types
import { TemplateType } from '@/components/template-builder-v3/types';

// Hooks
import { useGlobalPermission } from '@/hooks/useGlobalPermission';

// Utils
import { PERMISSIONS_LIST } from '@/utils/roles';
import Billing from './pages/Billing';
import ViewRole from './components/role-manager-v1/ViewRole';
import PaymentCancelled from '@/pages/PaymentCancelled';

// Lazy-loaded components
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Settings = lazy(() => import('@/pages/Settings'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const TemplateBuilder = lazy(() => import('@/components/template-builder-v3/Index'));
const Subscription = lazy(() => import('@/components/subscription-v2/Subscription'));
const AssetManager = lazy(() => import('@/components/asset-manager/AssetManager'));
const AuthPage = lazy(() => import('@/pages/Auth'));
const Help = lazy(() => import('@/pages/Help'));
const ComingSoon = lazy(() => import('@/pages/ComingSoon'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const NetworkErrorPage = lazy(() => import('@/pages/NetworkErrorPage'));
const TicketView = lazy(() => import('@/pages/TicketView'));
const AttributeSetPage = lazy(() => import('@/components/attribute-set-v2/AttributeSetPage'));
const AttributeDetails = lazy(() => import('@/components/attribute-set-v2/AttributeDetails'));
const ContentLibrary = lazy(() => import('@/pages/ContentLibrary'));
const SupportTicket = lazy(() => import('@/components/support-v2/SupportTicket'));
const PaymentStatus = lazy(() => import('@/pages/PaymentStatus'));
const RoleManager = lazy(() => import('@/pages/Roles'));
const VerifyUser = lazy(() => import('@/pages/VerifyUser'));
const GithubCallbackPage = lazy(() => import('@/pages/GithubCallback'));
const OnboardingFlow = lazy(() => import('@/components/onboarding/onboarding'));

const App: React.FC = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const isNotFoundPage = location.pathname === '/not-found';
  const isNetworkErrorPage = location.pathname === '/network-error';
  const isVerifyUserPage = location.pathname.startsWith('/verify-user');

  // Handle error pages outside of AuthProvider
  if (isNotFoundPage || isNetworkErrorPage || isVerifyUserPage) {
    return (
        <Suspense fallback={<div className='flex items-center justify-center w-full h-screen'>Loading...</div>}>
          <Routes>
            <Route path="/verify-user/:token" element={<VerifyUser />} />
            <Route path="/not-found" element={<NotFound />} />
            <Route path="/network-error" element={<NetworkErrorPage />} />
          </Routes>
        </Suspense>
    );
  }

  // Main app with auth context
  return (
    <AuthProvider>
      <WebSocketProvider>
        <Toaster position="top-center" reverseOrder={false} />
        <AppContent />
      </WebSocketProvider>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  useGlobalPermission();
  const isAuthPage = location.pathname === '/auth';
  const isComingSoonPage = location.pathname === '/coming-soon';
  const isPaymentSuccessPage = location.pathname === '/payment-success';
  const isPaymentCancelledPage = location.pathname === '/payment-cancelled';
  const isGithubCallbackPage = location.pathname === '/github/callback';
  const isOnboardingPage = location.pathname === '/onboarding';

  if (isAuthPage
    || location.pathname === '/forgot-password'
    || isPaymentSuccessPage
    || isPaymentCancelledPage
    || isGithubCallbackPage
    || isOnboardingPage) {
    return (
      <Suspense fallback={<div className='flex items-center justify-center w-full h-screen'>Loading...</div>}>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/github/callback" element={<GithubCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/payment-success" element={<PaymentStatus />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/onboarding" element={<OnboardingFlow />} />
        </Routes>
      </Suspense>
    );
  }

  if (isComingSoonPage) {
    return (
        <Suspense fallback={<div className='flex items-center justify-center w-full h-screen'>Loading...</div>}>
          <Routes>
            <Route path="/coming-soon" element={<ComingSoon />} />
          </Routes>
        </Suspense>
    );
  }

  if (!user || !user.accountInfo?.onboardingCompleted) {
    return null;
  }

  return (
    <ProjectProvider>
      <PageProvider>
        <Header />
        <Suspense fallback={<div className='flex items-center justify-center w-full h-screen'>Loading...</div>}>
          <Routes>
            <Route>
              <Route path="/" element=
                {
                  <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_DASHBOARD}>
                    <Dashboard />
                  </PermissionWrapper>
                } />
              <Route path="/page-content/:templateId" element=
                {
                  <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_CONTENT}>
                    <PageListMain />
                  </PermissionWrapper>
                } />

              <Route path="/page-content/:templateId/:pageId" element=
                {
                  <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_CONTENT}>
                    <PageFormMain />
                  </PermissionWrapper>
                } />
              <Route path="/template-builder" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_TEMPLATES}>
                  <TemplateBuilder mode={TemplateType.CUSTOM} />
                </PermissionWrapper>
              } />
              <Route path="/template-builder/:id?" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.CREATE_TEMPLATES}>
                  <TemplateBuilder mode={TemplateType.CUSTOM} />
                </PermissionWrapper>
              } />

              <Route path="/content-types" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_TEMPLATES}>
                  <TemplateBuilder mode={TemplateType.PAGE} />
                </PermissionWrapper>
              } />
              <Route path="/content-types/:id?" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.CREATE_TEMPLATES}>
                  <TemplateBuilder mode={TemplateType.PAGE} />
                </PermissionWrapper>
              } />
              {/* Settings Routes */}
              <Route path="/settings" element={
                <Navigate to="/settings/general" replace />
              } />
              <Route path="/settings/:tab" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_SETTINGS}>
                  <Settings />
                </PermissionWrapper>
              } />
              <Route path="/profile" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_PROFILE}>
                  <ProfilePage />
                </PermissionWrapper>
              } />
              <Route path="/asset-manager" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ASSETS}>
                  <AssetManager />
                </PermissionWrapper>
              } />
              <Route path="/subscription" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_SUBSCRIPTION}>
                  <Subscription />
                </PermissionWrapper>
              } />
              <Route path="/help" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_HELP_GUIDE}>
                  <Help />
                </PermissionWrapper>
              } />
              {/* Role Manager Routes */}
              <Route path="/roles" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ROLES}>
                  <RoleManager />
                </PermissionWrapper>
              } />
              <Route path="/roles/view" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ROLES}>
                  <ViewRole/>
                </PermissionWrapper>
              } />
              <Route path="/roles/edit/:roleId" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.UPDATE_ROLES}>
                  <RoleManager />
                </PermissionWrapper>
              } />
              <Route path="/roles/create" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.CREATE_ROLES}>
                  <RoleManager />
                </PermissionWrapper>
              } />
              {/* Support Routes */}
              <Route path="/support" element={<Navigate to="/support/dashboard" replace />} />
              <Route path="/support/:tab" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_SUPPORT}>
                  <SupportTicket />
                </PermissionWrapper>
              } />
              <Route path="/support/ticket/:id" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_TICKETS}>
                  <TicketView />
                </PermissionWrapper>
              } />
              {/* Attribute Set Routes */}
              <Route path="/attribute-sets" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ATTRIBUTES}>
                  <AttributeSetPage />
                </PermissionWrapper>
              } />
              <Route path="/attribute-sets/:attributeSetId" element={
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ATTRIBUTES}>
                  <AttributeDetails />
                </PermissionWrapper>
              } />
            </Route>
            {/* Content Library Routes */}
            <Route path="/content-library" element={
              <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_LIBRARY}>
                <ContentLibrary />
              </PermissionWrapper>
            } />
            {/* Billing Routes */}
            <Route path="/billing" element={
              // TO-DO :- need to change to VIEW_BILLING
                <PermissionWrapper permission={PERMISSIONS_LIST.VIEW_ASSETS}>
                  <Billing />
                </PermissionWrapper>
              } />

            <Route path="/some-component" element={<SomeComponent />} />
            <Route path="*" element={<Navigate to="/not-found" replace />} />
          </Routes>
        </Suspense>
      </PageProvider>
    </ProjectProvider>
  );
};

export default App;
